import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchHomeFeed, searchHomeFeed } from "../services/soundcloud/homeService";

const SEARCH_DEBOUNCE_MS = 420;
const PAGE_SIZE = 15;

const EMPTY_HOME_DATA = {
  heroAlbum: null,
  recommendedCards: [],
  relatedTracks: [],
  searchCollections: {
    users: [],
    playlists: [],
    albums: [],
  },
  pagination: {
    hasMore: false,
    nextOffset: null,
    pageSize: 0,
    total: 0,
    cursor: null,
    collections: {
      users: { hasMore: false, nextOffset: null, total: 0 },
      playlists: { hasMore: false, nextOffset: null, total: 0 },
      albums: { hasMore: false, nextOffset: null, total: 0 },
    },
  },
};

function normalizeErrorMessage(error) {
  if (!error) return "Unknown error.";
  return error.message || "Unable to load home feed.";
}

function mergeById(currentItems, nextItems) {
  const merged = [...currentItems];
  const seen = new Set(currentItems.map((item) => item?.id).filter(Boolean));

  nextItems.forEach((item) => {
    if (!item?.id || !seen.has(item.id)) {
      merged.push(item);
      if (item?.id) seen.add(item.id);
    }
  });

  return merged;
}

function useHomeFeed({ query, enabled = true } = {}) {
  const normalizedQuery = useMemo(() => String(query || "").trim(), [query]);
  const requestIdRef = useRef(0);
  const cacheRef = useRef(new Map());
  const timeoutRef = useRef(null);
  const dataRef = useRef(EMPTY_HOME_DATA);
  const [data, setData] = useState(EMPTY_HOME_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingCollection, setLoadingCollection] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const load = useCallback(
    async ({
      forceRefresh = false,
      append = false,
      appendCollection = "",
      includeTracks,
      includeUsers,
      includePlaylists,
      includeAlbums,
      resetCollection = false,
    } = {}) => {
      if (!enabled) return;

      const cacheKey = normalizedQuery || "__home__";
      if (!append && !appendCollection && !forceRefresh && cacheRef.current.has(cacheKey)) {
        setData(cacheRef.current.get(cacheKey));
        setError("");
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const currentRequestId = requestIdRef.current + 1;
      requestIdRef.current = currentRequestId;
      if (append || appendCollection) {
        setIsLoadingMore(true);
        setLoadingCollection(appendCollection || "tracks");
      } else {
        setIsLoading(true);
      }
      setError("");

      try {
        let nextData;
        if (normalizedQuery) {
          const currentData = dataRef.current;
          const isCollectionRequest = Boolean(appendCollection);
          const offset = append && !isCollectionRequest
            ? currentData.pagination.nextOffset || currentData.recommendedCards.length
            : 0;

          const usersItems = currentData.searchCollections.users || [];
          const playlistsItems = currentData.searchCollections.playlists || [];
          const albumsItems = currentData.searchCollections.albums || [];

          const usersOffset = appendCollection === "users"
            ? resetCollection
              ? 0
              : currentData.pagination.collections.users.nextOffset || usersItems.length
            : 0;
          const playlistsOffset = appendCollection === "playlists"
            ? resetCollection
              ? 0
              : currentData.pagination.collections.playlists.nextOffset || playlistsItems.length
            : 0;
          const albumsOffset = appendCollection === "albums"
            ? resetCollection
              ? 0
              : currentData.pagination.collections.albums.nextOffset || albumsItems.length
            : 0;

          nextData = await searchHomeFeed(normalizedQuery, {
            offset,
            limit: PAGE_SIZE,
            includeTracks:
              includeTracks ??
              (!isCollectionRequest),
            includeUsers:
              includeUsers ??
              (appendCollection === "users"),
            includePlaylists:
              includePlaylists ??
              (appendCollection === "playlists"),
            includeAlbums:
              includeAlbums ??
              (appendCollection === "albums"),
            usersOffset,
            playlistsOffset,
            albumsOffset,
            heroAlbum: currentData.heroAlbum,
          });
        } else {
          const currentData = dataRef.current;
          nextData = await fetchHomeFeed({
            offset: append ? currentData.pagination.nextOffset || 0 : 0,
            limit: PAGE_SIZE,
            cursor: append ? currentData.pagination.cursor : null,
            heroAlbum: append ? currentData.heroAlbum : null,
          });
        }

        if (requestIdRef.current !== currentRequestId) return;

        const currentData = dataRef.current;
        const mergedData = append
          ? {
            ...nextData,
            recommendedCards: mergeById(
              currentData.recommendedCards,
              nextData.recommendedCards
            ),
            relatedTracks: mergeById(currentData.relatedTracks, nextData.relatedTracks),
            searchCollections: {
              users: currentData.searchCollections.users.length
                ? currentData.searchCollections.users
                : nextData.searchCollections.users,
              playlists: currentData.searchCollections.playlists.length
                ? currentData.searchCollections.playlists
                : nextData.searchCollections.playlists,
              albums: currentData.searchCollections.albums.length
                ? currentData.searchCollections.albums
                : nextData.searchCollections.albums,
            },
            pagination: {
              ...nextData.pagination,
              collections: {
                users: currentData.pagination.collections.users,
                playlists: currentData.pagination.collections.playlists,
                albums: currentData.pagination.collections.albums,
              },
            },
          }
          : appendCollection
            ? {
              ...currentData,
              searchCollections: {
                users:
                  appendCollection === "users"
                    ? mergeById(currentData.searchCollections.users, nextData.searchCollections.users)
                    : currentData.searchCollections.users,
                playlists:
                  appendCollection === "playlists"
                    ? mergeById(
                      currentData.searchCollections.playlists,
                      nextData.searchCollections.playlists
                    )
                    : currentData.searchCollections.playlists,
                albums:
                  appendCollection === "albums"
                    ? mergeById(currentData.searchCollections.albums, nextData.searchCollections.albums)
                    : currentData.searchCollections.albums,
              },
              pagination: {
                ...currentData.pagination,
                collections: {
                  users:
                    appendCollection === "users"
                      ? nextData.pagination.collections.users
                      : currentData.pagination.collections.users,
                  playlists:
                    appendCollection === "playlists"
                      ? nextData.pagination.collections.playlists
                      : currentData.pagination.collections.playlists,
                  albums:
                    appendCollection === "albums"
                      ? nextData.pagination.collections.albums
                      : currentData.pagination.collections.albums,
                },
              },
            }
            : nextData;

        cacheRef.current.set(cacheKey, mergedData);

        setData(mergedData);
      } catch (loadError) {
        if (requestIdRef.current !== currentRequestId) return;
        setError(normalizeErrorMessage(loadError));
      } finally {
        if (requestIdRef.current === currentRequestId) {
          if (append || appendCollection) {
            setIsLoadingMore(false);
            setLoadingCollection("");
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    [enabled, normalizedQuery]
  );

  const refresh = useCallback(() => {
    load({ forceRefresh: true, includeTracks: true });
  }, [load]);

  const loadMoreSearch = useCallback(() => {
    if (isLoading || isLoadingMore || !data.pagination.hasMore) return;
    load({ append: true, includeTracks: true });
  }, [data.pagination.hasMore, isLoading, isLoadingMore, load]);

  const loadMoreCollection = useCallback(
    (collectionType) => {
      if (!normalizedQuery || isLoading || isLoadingMore) return;
      const target = data.pagination.collections[collectionType];
      const items = data.searchCollections[collectionType] || [];
      const needsFirstFetch = items.length === 0;
      if (!needsFirstFetch && !target?.hasMore) return;

      load({
        appendCollection: collectionType,
        includeTracks: false,
        includeUsers: collectionType === "users",
        includePlaylists: collectionType === "playlists",
        includeAlbums: collectionType === "albums",
        resetCollection: needsFirstFetch,
      });
    },
    [data.pagination.collections, data.searchCollections, isLoading, isLoadingMore, load, normalizedQuery]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    timeoutRef.current = window.setTimeout(() => {
      load();
    }, normalizedQuery ? SEARCH_DEBOUNCE_MS : 0);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, load, normalizedQuery]);

  return { data, isLoading, isLoadingMore, loadingCollection, error, refresh, loadMoreSearch, loadMoreCollection };
}

export default useHomeFeed;
